import { useFormContext, useWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { Skeleton, Tooltip } from '@hopskipdrive/react-components';
import { analyticsRiderServicesFieldChanged } from '@rideiq/api/services/Analytics';
import { colorTokens } from '@rideiq/shared/components/variables';
import styled, { css } from 'styled-components';

import { trackFullStoryEvent } from '../../../../../../vendor/features/FullStory/FullStory.service';
import type {
  SupportSettingCatalogItem,
  SupportSettingCategory,
} from '../../../../../api/services/RiderSupportSettings';
import { TOOLTIP_HOVER_DELAY_MS } from '../../../constants';
import type { RiderFormValues, SupportSetting } from '../../../types';

export type PillProps = {
  setting: SupportSettingCatalogItem;
  categoryName: SupportSettingCategory['name'];
};

export const Pill = ({ setting, categoryName }: PillProps) => {
  const { setValue } = useFormContext<RiderFormValues>();
  const entries = (useWatch<RiderFormValues, 'supportSettings'>({ name: 'supportSettings' }) ?? []) as SupportSetting[];
  const { slug } = useParams<{ slug?: string }>();

  const index = entries.findIndex((s) => s.supportType === setting.supportType);
  const selected = index >= 0 ? entries[index].selected : false;

  const handleToggle = () => {
    const next = [...entries];
    let newSelected: boolean;

    if (index >= 0) {
      newSelected = !entries[index].selected;
      next[index] = { ...next[index], selected: newSelected };
    } else {
      newSelected = true;
      next.push({ supportType: setting.supportType, details: '', selected: true });
    }
    setValue('supportSettings', next, { shouldDirty: true });
    analyticsRiderServicesFieldChanged({
      field_name: setting.supportType,
      new_value: newSelected,
      rider_id: slug ?? null,
    });

    if (newSelected) {
      trackFullStoryEvent('rider_profile_safety_setting_selected', {
        setting_category: categoryName,
        setting_name: setting.label,
      });
    }
  };

  return (
    <Tooltip title={setting.tooltip} enterDelay={TOOLTIP_HOVER_DELAY_MS} enterNextDelay={TOOLTIP_HOVER_DELAY_MS}>
      <PillButton
        type="button"
        $selected={selected}
        aria-pressed={selected}
        data-testid={`pill-${setting.supportType}`}
        onClick={handleToggle}
      >
        {setting.label}
      </PillButton>
    </Tooltip>
  );
};

const PillButton = styled.button<{ $selected: boolean }>`
  height: 24px;
  padding: 0 12px;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  ${({ $selected }) =>
    $selected
      ? css`
          border: 1px solid ${colorTokens.primary.main};
          background-color: ${colorTokens.primary.main};
          color: ${colorTokens.text.contrast};
        `
      : css`
          border: 1px solid ${colorTokens.border.subtle};
          background-color: ${colorTokens.background.default};
          color: ${colorTokens.text.dark};
        `}
`;

export const SkeletonPill = styled(Skeleton)`
  height: 24px;
  padding: 0 12px;
  border-radius: 16px;
`;
